<template>
    <div class="paypal-container">
        <div v-show="paypalReady" id="payPalButtonContainer"></div>
        <div id="applepay-container"></div>
        <div v-if="!paypalReady && hasClientId">
            <span class="paypal-loader"></span>
        </div>
    </div>

</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {DateTime} from "luxon";
import ApiRequest from "../../../../utils/api-request";

export default {
    name: 'Paypal',
    props: {
        refContactInformation: {
            type: Object,
            default: null
        },
        referrer: {
            type: String,
            default: ''
        },
        isProduct: {
            type: Boolean,
            default: false
        },
        productShippingAddress: {
            type: String,
            default: ''
        },
        productVariantSelected: {
            type: String,
            default: ''
        },
        productShippingMethod: {
            type: String,
            default: ''
        },
        product: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            payload: {},
            order_id: 0,
            paypalReady: false,
            currency: 'USD',
            hasClientId: true
        }
    },

    computed: {
        ...mapGetters('app', ['firstName', 'lastName', 'email', 'phone']),
        ...mapGetters('app', ['paypalClientId']),
        ...mapGetters('filter', ['journeyDate']),
        ...mapGetters('cart', ['isEmpty', 'totalPrice', 'totalTax', 'totalDiscount']),
        ...mapGetters('cart', {
            usedPromoCode: 'promoCode',
            cartBikes: 'bikes',
            cartEvents: 'events',
            cartTours: 'tours',
            cartAccessories: 'accessories',
            cartMerchandises: 'merchandises',
            cartAdditionalOptions: 'additionalOptions'
        }),
    },

    mounted() {
        this.loadPayPal()
    },

    methods: {
        ...mapActions('cart', ['usePromoCode']),
        ...mapActions('payment', ['initCreditCardPayment']),
        errorNotify(message) {
            if (message) {
                this.$notify({
                    text: message,
                    type: 'error'
                });
            } else {
                this.$notify({
                    type: 'error',
                    title: 'Server error!',
                    text: 'Something went wrong. Please contact our support service',
                    duration: 5000
                });
            }
        },

        fetchCreatePaypalOrder(payload) {
            const $self = this;
            return new Promise((resolve, reject) => {
                new ApiRequest().createPaypalOrder(payload).then((response) => {
                    if (!response.data.status) {
                        $self.errorNotify(response.data.message);
                        resolve('');
                        return;
                    }
                    $self.order_id = response.data.order_id;
                    resolve(response.data.data.id);
                }).catch((error) => {
                    $self.errorNotify('');
                    reject(error);
                });
            });
        },

        fetchCapturePaypalOrder(payload) {
            const $self = this;
            return new Promise((resolve, reject) => {
                new ApiRequest().capturePaypalOrder(payload).then((response) => {
                    if (!response.data.status) {
                        $self.errorNotify(response.data.message);
                        resolve('');
                        return;
                    }

                    this.initCreditCardPayment();

                    if($self.isProduct) {

                        try {
                            gtag('event', 'conversion', {
                                'send_to': window.gtag_send_to,
                                'value': (($self.product.price) / 100),
                                'currency': 'USD',
                                'transaction_id': response.data.info.qr,
                                'ref': $self.referrer
                            });
                        } catch (e) {
                            console.error(e.message);
                        }

                        location.href = '/checkout-success/thank-you-purchase/' + btoa(JSON.stringify({data: response.data, product: $self.product.meta.title}));
                    } else {

                        try {
                            gtag('event', 'conversion', {
                                'send_to': window.gtag_send_to,
                                'value': (($self.totalPrice + $self.totalTax - $self.totalDiscount) / 100),
                                'currency': 'USD',
                                'transaction_id': response.data.info.qr,
                                'ref': $self.referrer
                            });
                        } catch (e) {
                            console.error(e.message);
                        }

                        location.href = '/checkout-success/thank-you-' + $self.payload.type + '/' + btoa(JSON.stringify({ data: response.data }));
                    }



                    resolve(response.data);
                }).catch((error) => {
                    $self.errorNotify('');
                    reject(error);
                });
            });
        },

        fetchCancelPaypalOrder(payload) {
            const $self = this;
            return new Promise((resolve, reject) => {
                new ApiRequest().cancelPaypalOrder(payload).then((response) => {
                    $self.order_id = 0;
                    resolve(response.data);
                }).catch((error) => {
                    $self.order_id = 0;
                    reject(error);
                });
            });
        },

        loadPayPal() {
            if (window.paypal === undefined) {

                if(!this.paypalClientId) {
                    this.hasClientId = false;
                    return;
                }

                let paypalScript = document.createElement('script');
                // let applePayScript = document.createElement('script');
                // applePayScript.setAttribute('src', 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js')


                paypalScript.setAttribute('src', `https://www.paypal.com/sdk/js?client-id=${this.paypalClientId}&currency=${this.currency}&buyer-country=US&components=buttons&disable-funding=paylater,credit`);
                document.head.appendChild(paypalScript);

                paypalScript.onload = () => {
                    this.paypalReady = true;
                    this.initPaypalElements();
                }
            } else {
                this.paypalReady = true;
                this.initPaypalElements();
            }
        },

        initPaypalElements: function () {
            const $self = this;
            window.paypal.Buttons({
                style: {
                    layout: 'horizontal',
                    label: 'pay',
                    height: 55
                },
                createOrder: function () {
                    if($self.refContactInformation) {
                        if ( !$self.refContactInformation.validate()) {
                            return false;
                        }
                    } else {
                        if ( !$self.$parent.validate()) {
                            return false;
                        }
                    }

                    // payment's main fields
                    let payload = {
                        debug: process.env.NODE_ENV === 'development',
                        ref: $self.referrer,
                        discount_code: $self.usedPromoCode ? $self.usedPromoCode.code : null,
                        firstName: $self.firstName,
                        lastName: $self.lastName,
                        email: $self.email,
                        phone: $self.phone,
                    }

                    // this part of JSON data object is common
                    let data = {
                        accessories: $self.cartAccessories.map(i => ({
                            accessory_id: i.item.accessory_id,
                            price_id: i.priceOption.price_id,
                            qty: i.amount
                        })),
                        merchandises: $self.cartMerchandises.map(i => ({
                            merchandise_id: i.item.id,
                            size: i.priceOption.size,
                            qty: i.amount
                        })),
                        additional_options: $self.cartAdditionalOptions.map(i => ({
                            additional_option_id: i.item.id,
                            qty: i.amount
                        }))
                    }

                    // structure for rentals checkout
                    if ($self.cartBikes.length && !$self.isProduct) {
                        payload = {
                            ...payload,
                            start: DateTime.fromISO($self.journeyDate).toFormat('yyyy-MM-dd') + " 00:00:00",
                            type: 'rental',
                            data: JSON.stringify({
                                ...data,
                                bikes: $self.cartBikes.map(i => ({
                                    type_id: i.item.type_id,
                                    price_id: i.priceOption.price_id,
                                    qty: i.amount,
                                    insurance_qty: i.insuranceAmount
                                }))
                            })
                        }
                    }

                    // structure for rentals checkout
                    if ($self.cartEvents.length && !$self.isProduct) {
                        payload = {
                            ...payload,
                            start: DateTime.fromISO($self.journeyDate).toFormat('yyyy-MM-dd') + " 00:00:00",
                            type: 'event',
                            data: JSON.stringify({
                                ...data,
                                event_id: $self.cartEvents[0].item.event_id,
                                bikes: $self.cartEvents.map(i => ({
                                    type_id: i.item.type_id,
                                    price_id: i.priceOption.price_id,
                                    qty: i.amount,
                                    insurance_qty: i.insuranceAmount
                                }))
                            })
                        }
                    }

                    // structure for tour checkout
                    if ($self.cartTours.length && !$self.isProduct) {
                        payload = {
                            ...payload,
                            start: DateTime.fromISO($self.cartTours[0].slot.date).toFormat('yyyy-MM-dd') + ' ' + $self.cartTours[0].slot.time,
                            type: 'tour',
                            data: JSON.stringify({
                                ...data,
                                tour_id: $self.cartTours[0].item.id,
                                bikes: $self.cartTours[0].item.bike_types.filter(j => (j.amount > 0)).map(j => ({
                                    type_id: j.type_id,
                                    price_id: null,
                                    qty: j.amount,
                                    insurance_qty: j.insuranceAmount
                                }))
                            })
                        }
                    }

                    if($self.isProduct) {
                        payload = {
                            debug: process.env.NODE_ENV === 'development',
                            ref: $self.referrer,
                            firstName: $self.firstName,
                            lastName: $self.lastName,
                            email: $self.email,
                            phone: $self.phone,
                            discount_code: null,
                            type: 'product',
                            shipping_method: $self.productShippingMethod,
                            data: JSON.stringify({
                                products: [
                                    {
                                        id: $self.productVariantSelected,
                                        qty: 1
                                    }
                                ]
                            })
                        }

                        if ($self.productShippingMethod === 'domestic_shipping') {
                            payload = {
                                ...payload,
                                shipping_address: $self.productShippingAddress
                            }
                        }
                    }

                    $self.payload = payload;

                    return $self.fetchCreatePaypalOrder(payload);
                },
                onApprove: function (data, actions) {
                    return $self.fetchCapturePaypalOrder({
                        paypal_order_id: data.orderID,
                        system_order_id: $self.order_id,
                        debug: process.env.NODE_ENV === 'development',
                        payload_data: $self.payload
                    })
                },
                onCancel(data) {
                    return $self.fetchCancelPaypalOrder({
                        paypal_order_id: data.orderID,
                        system_order_id: $self.order_id,
                    })
                },
                onError: function (err) {
                    console.log(err);
                },
                // onRendered: function () {
                //
                // }
            }).render('#payPalButtonContainer');
            // if (!window.ApplePaySession) {
            //     console.error('This device does not support Apple Pay');
            // }
            // if (!window.ApplePaySession.canMakePayments()) {
            //     console.error('This device is not capable of making Apple Pay payments');
            // }
            // const applepay = window.paypal.Applepay();
            // applepay.config()
            //     .then(applepayConfig => {
            //         if (applepayConfig.isEligible) {
            //             document.getElementById("applepay-container").innerHTML = '<apple-pay-button id="btn-appl" buttonstyle="black" type="buy" locale="en">';
            //         }
            //     })
            //     .catch(applepayConfigError => {
            //         console.error('Error while fetching Apple Pay configuration.');
            //     });
        },
    }
}
</script>
